import { cn } from "./cn";
import {
  GlobeIcon,
  MailIcon,
  MarketplaceIcon,
  MessageIcon,
  WhatsAppIcon,
} from "./icons";
import type { ChannelType } from "../../types";

/**
 * Which route a ticket arrived on, as one glyph.
 *
 * A column of icons rather than a column of words. The queue is read by
 * scanning it, not by reading it, and "Gmail" repeated eighty times down a
 * table is eighty words that carry no information -- the whole value of this
 * is the two rows that are *not* email standing out from the rest.
 *
 * Which is also why it is grey and not branded. A green WhatsApp mark in a
 * queue would outrank the SLA clock and the unread weight, both of which are
 * about whether a ticket needs somebody now; how it arrived is context, and
 * context belongs at the weight of the timestamps beside it.
 *
 * The channel's own name is the title, because two Gmail channels draw the
 * same envelope and which mailbox a ticket landed in is the question that
 * follows immediately after noticing it.
 *
 * `className` styles the wrapper, not the glyph, so a caller can size it and
 * place it in a flex row in one prop -- the SVG fills whatever it is given.
 */
export function ChannelIcon({
  type,
  name,
  className = "size-3.5 text-muted",
}: {
  type: ChannelType;
  /** The channel's display name, for the tooltip. */
  name: string;
  className?: string;
}) {
  const Glyph =
    type === "whatsapp"
      ? WhatsAppIcon
      : type === "sms"
        ? MessageIcon
        : type === "webform"
          ? GlobeIcon
          : type === "ebay"
            ? MarketplaceIcon
            : MailIcon;

  return (
    <span
      title={`${name} (${LABELS[type]})`}
      /*
       * `relative` is load-bearing and not decoration. The label below is
       * `sr-only`, which Tailwind implements as `position: absolute`, and an
       * absolutely positioned box is contained by its nearest *positioned*
       * ancestor -- not by whichever scroll container happens to be in the way.
       * Without this the label's containing block is somewhere up near the app
       * frame, so it sits at its static position in document coordinates: one
       * per row, the lowest of them a few thousand pixels down a page that then
       * grows to match. That is the bug in aed0cbc, in a component that would
       * have reintroduced it eighty times a queue.
       */
      className={cn("relative inline-flex", className)}
    >
      <Glyph className="size-full" />
      {/*
        The type in words, for a screen reader. `title` alone is announced
        inconsistently, and a row read aloud as "Acme Ltd, image, switch not
        powering on" has lost the column entirely.
      */}
      <span className="sr-only">{LABELS[type]}</span>
    </span>
  );
}

const LABELS: Record<ChannelType, string> = {
  gmail: "Email",
  whatsapp: "WhatsApp",
  sms: "SMS",
  webform: "Web form",
  ebay: "eBay",
};
